import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../../../shared/contexts/LanguageContext";
import { useStepRedirect, skipNextStepRedirect } from "../../../hooks/useStepRedirect";
import ResponsiveFormLayout from "../../../shared/components/layout/ResponsiveFormLayout";
import CooperationLoader from "../../../shared/components/feedback/CooperationLoader";
import ModalPortal from "../../../shared/components/ui/ModalPortal";
import { useProgressWithAutoRefresh } from "../../../shared/contexts/ProgressContext";

const DoctorHomeScreen: React.FC = () => {
  // Handle all redirects (clinic_id, active requests, corrections, etc.)
  // This is the single source of truth for routing based on progress
  useStepRedirect();

  const { t, isRTL } = useLanguage();
  const navigate = useNavigate();
  const { isLoading: progressLoading, doctorInfo, hasClinic } = useProgressWithAutoRefresh();
  const [showOnboardConfirm, setShowOnboardConfirm] = useState(false);

  // Both home options start the same clinic-info flow; only the stored
  // procedure decides whether it runs as a registration or an onboarding.
  const startClinicFlow = (procedure: "OPEN_CLINIC" | "REGISTER_EXISTING_CLINIC") => {
    localStorage.setItem("doctor_procedure", procedure);
    skipNextStepRedirect();
    navigate("/clinic_info_registration");
  };

  if (progressLoading) {
    return (
      <div className="fixed top-0 left-0 right-0 bottom-0 bg-white flex justify-center items-center">
        <CooperationLoader />
      </div>
    );
  }

  // Note: hasActiveProgress and hasPendingActions kept for potential future use
  // const hasActiveProgress = progress?.has_active_requests && progress.active_requests.length > 0;
  // const hasPendingActions = progress?.pending_actions && progress.pending_actions.length > 0;

  return (
    <ResponsiveFormLayout
      showLanguageToggle={true}
      icon="/ima_logo.png"
      iconSize="w-24 h-24"
      title={t("welcome_doctor")}
      subtitle={
        <div className="flex flex-col items-center">
          {doctorInfo?.full_name_ar && <span dir="rtl">{doctorInfo.full_name_ar}</span>}
          {doctorInfo?.full_name && <span>{doctorInfo.full_name}</span>}
        </div>
      }
      maxHeight="90vh"
    >
      <div className="w-full space-y-6">
        {/* Pending Actions - Show at top if there are any actions needed */}
        {/* Commented out - using dedicated requests page instead
        {hasPendingActions && (
          <PendingActions
            actions={progress!.pending_actions}
            maxItems={3}
          />
        )}
        */}

        {/* Active Requests Progress - Show progress cards for active requests */}
        {/* Commented out - using dedicated requests page instead
        {hasActiveProgress && (
          <div className="space-y-4">
            <h2 className={`text-lg font-semibold ${isRTL ? "text-right" : "text-left"}`}>
              {t("active_requests") || "Active Requests"}
            </h2>
            {progress!.active_requests.map((request) => (
              <RequestProgressCard
                key={request.request_id}
                request={request}
                onDocumentUpload={handleDocumentUpload}
                defaultExpanded={progress!.active_requests.length === 1}
              />
            ))}
          </div>
        )}
        */}

        {/* Registration options */}
        <div className="space-y-4">
            <div className="text-center py-8">
              <div className="text-6xl mb-4">🏥</div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                {t("no_clinics_yet")}
              </h2>
              <p className="text-gray-600">
                {t("get_started_message")}
              </p>
            </div>

            {/* Warn that an existing open clinic must be closed before opening a new one */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white text-xs">ℹ</span>
                </div>
                <p className={`flex-1 text-sm text-amber-800 ${isRTL ? "text-right" : "text-left"}`}>
                  {t("clinic_choice_warning")}
                </p>
              </div>
            </div>

            {/* Register New Clinic */}
            <button
              onClick={() => startClinicFlow("OPEN_CLINIC")}
              className="w-full min-h-[56px] px-6 py-4 rounded-2xl text-white text-base font-semibold transition-all duration-200 shadow-md hover:shadow-lg"
              style={{ backgroundColor: "#2E5D7D" }}
            >
              {t("open_new_clinic")}
            </button>

            {/* Onboard Existing Clinic — confirm first, this option needs an
                existing clinic opening license. */}
            <button
              onClick={() => setShowOnboardConfirm(true)}
              className="w-full min-h-[56px] px-6 py-4 rounded-2xl border-2 border-[#2E5D7D] text-[#2E5D7D] text-base font-semibold transition-all duration-200 hover:bg-[#2E5D7D] hover:text-white"
            >
              {t("onboard_existing_clinic")}
            </button>

            {/* Close Existing Clinic (only for doctors with NO registered clinic;
                doctors who have a clinic use "close my registered clinic" instead). */}
            {!hasClinic && (
              <button
                onClick={() => { skipNextStepRedirect(); navigate("/existing-clinic-closure"); }}
                className="w-full min-h-[56px] px-6 py-4 rounded-2xl border-2 border-red-500 text-red-500 text-base font-semibold transition-all duration-200 hover:bg-red-500 hover:text-white"
              >
                {t("close_existing_clinic") || "Close Existing Clinic"}
              </button>
            )}
          </div>
      </div>

      {/* Onboarding Confirmation Dialog */}
      {showOnboardConfirm && (
        <ModalPortal>
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
            dir={isRTL ? "rtl" : "ltr"}
          >
            <div className="bg-white rounded-2xl p-6 max-w-sm w-full mx-4 shadow-2xl">
              <div className="text-center mb-4">
                <span className="text-5xl">⚠️</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3 text-center">
                {t("onboard_confirm_title")}
              </h3>
              <p className={`text-gray-600 mb-6 text-sm ${isRTL ? "text-right" : "text-left"}`}>
                {t("onboard_confirm_message")}
              </p>
              <div className="flex flex-col gap-3">
                <button
                  onClick={() => startClinicFlow("REGISTER_EXISTING_CLINIC")}
                  className="w-full px-4 py-3 rounded-xl text-white font-medium transition-all"
                  style={{ backgroundColor: "#2E5D7D" }}
                >
                  {t("onboard_confirm_proceed")}
                </button>
                <button
                  onClick={() => setShowOnboardConfirm(false)}
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-all"
                >
                  {t("cancel") || "Cancel"}
                </button>
              </div>
            </div>
          </div>
        </ModalPortal>
      )}
    </ResponsiveFormLayout>
  );
};

export default DoctorHomeScreen;
